/**
 * Теги чата в правой панели — одной строкой: подпись, метки с крестиком и кнопка «+». По «+» — список уже существующих
 * тегов (клик — тег стоит на чате) и поле для нового названия. Теги общие для всех сотрудников,
 * по ним фильтруется список чатов (воронка у поиска). `POST`/`DELETE /api/chats/{id}/tags`.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, X } from "lucide-react";
import { useState } from "react";

import { addChatTag, loadChatTags, removeChatTag } from "@/lib/clientChats";

export function ChatTags({ chatId, tags }: { chatId: number; tags: string[] }) {
  const qc = useQueryClient();
  const [draft, setDraft] = useState("");
  const [open, setOpen] = useState(false);
  // Справочник тегов — на минуту: правки тегов здесь же его и сбрасывают (`refresh`).
  const all = useQuery({ queryKey: ["client-chat-tags"], queryFn: loadChatTags, staleTime: 60_000 });
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ["client-chat", chatId] });
    void qc.invalidateQueries({ queryKey: ["client-chats"] });
    void qc.invalidateQueries({ queryKey: ["client-chat-tags"] });
    void qc.invalidateQueries({ queryKey: ["chat-actions", chatId] });
  };
  const add = useMutation({ mutationFn: (name: string) => addChatTag(chatId, name), onSettled: refresh });
  const remove = useMutation({ mutationFn: (name: string) => removeChatTag(chatId, name), onSettled: refresh });
  const failure = add.error ?? remove.error;
  const has = new Set(tags.map((t) => t.toLowerCase()));
  const free = (all.data?.tags ?? []).filter((t) => !has.has(t.name.toLowerCase()));

  return (
    <div
      className="rounded-[var(--radius-card)] border border-border bg-bg-subtle px-3 py-1.5 text-[12.5px]"
      data-annot="теги чата"
    >
      <div className="flex flex-wrap items-center gap-1">
        <span className="mr-1 text-[13px] font-medium">Теги</span>
        {tags.map((t) => (
          <span
            key={t}
            className="inline-flex items-center gap-1 rounded-full border border-accent/40 bg-accent/10 px-2 py-0.5 text-[12px] text-accent-text"
          >
            {t}
            <button
              type="button"
              onClick={() => remove.mutate(t)}
              disabled={remove.isPending}
              title="Снять тег"
              className="text-fg-faint hover:text-err"
            >
              <X size={12} aria-hidden />
              <span className="sr-only">Снять тег {t}</span>
            </button>
          </span>
        ))}
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          title="Добавить тег"
          aria-expanded={open}
          className={`grid size-6 place-items-center rounded-full border ${
            open ? "border-accent text-accent-text" : "border-border text-fg-muted hover:bg-bg-hover hover:text-fg"
          }`}
          data-annot="добавить тег"
        >
          <Plus size={14} aria-hidden />
          <span className="sr-only">Добавить тег</span>
        </button>
      </div>
      {open && (
        <div className="mt-2 border-t border-border pt-2">
          {/* Существующие теги, которых на этом чате ещё нет: клик — тег поставлен. */}
          {all.isPending && <div className="text-fg-muted">Загрузка…</div>}
          {all.isError && <div className="text-err">Не удалось загрузить теги</div>}
          {free.length > 0 && (
            <div className="mb-2 flex flex-wrap gap-1">
              {free.map((t) => (
                <button
                  key={t.name}
                  type="button"
                  onClick={() => add.mutate(t.name)}
                  disabled={add.isPending}
                  title={`Поставить тег · чатов с ним: ${t.chats}`}
                  className="rounded-full border border-border px-2 py-0.5 text-[12px] text-fg-muted hover:border-accent hover:text-fg disabled:opacity-50"
                >
                  {t.name}
                </button>
              ))}
            </div>
          )}
          <form
            className="flex gap-1"
            onSubmit={(e) => {
              e.preventDefault();
              if (!draft.trim()) return;
              add.mutate(draft.trim());
              setDraft("");
            }}
          >
            <input
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              maxLength={64}
              placeholder="Новый тег"
              aria-label="Название нового тега"
              className="h-7 min-w-0 flex-1 rounded-[var(--radius-ui)] border border-border bg-transparent px-2 text-[12px]"
            />
            <button
              type="submit"
              disabled={!draft.trim() || add.isPending}
              className="h-7 rounded-[var(--radius-ui)] border border-border px-2 text-[12px] text-fg-muted hover:bg-bg-hover disabled:opacity-50"
            >
              Создать
            </button>
          </form>
        </div>
      )}
      {failure && <div className="mt-1 text-err">{failure.message}</div>}
    </div>
  );
}
